import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Icons, Item, MenuOptionItem, MenuTriggerButton } from "widgets";

import { getRangeLength } from "@/app-utils/range.ts";
import { capitalizeFirstLetterOfAllWords, pluralizeWord } from "@/app-utils/string.ts";
import { type InputTableQuerySpec, type SequenceFeatureTypes } from "@/graphql.ts";
import { forwardContigRangeToBlast, forwardFeaturesToBlast } from "@/redux/modules/blast.ts";
import { type IGenomeBrowserState } from "@/redux/modules/genome-browser.ts";
import { type ITableData } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import {
  ENTITY_TYPE,
  FEATURE_DISPLAY_NAME,
  getSequenceFeatureTypeDisplayName,
  MAXIMUM_BLAST_SEQUENCE_LENGTH,
  MINIMUM_BLAST_SEQUENCE_LENGTH,
  VIEW,
} from "@/settings/settings.ts";

interface BlastButtonHeaderProps {
  viewType: string;
  entity: any;
  entityType: string;
  activeTables: ITableData[];
  genomeBrowser: IGenomeBrowserState;
}

/**
 * The BLAST button component, meant to be extended to other kinds of analysis.
 */
export function BlastButtonHeader(props: BlastButtonHeaderProps) {
  const dispatch = useDispatch<ReduxDispatch>();
  const genomeBrowser: IGenomeBrowserState = props.genomeBrowser;
  const range = genomeBrowser.selectedRange || genomeBrowser.range;
  const rangeLength = range ? getRangeLength(range) : 0;

  const sequenceFeatureTypes = useSelector<IAppState, SequenceFeatureTypes>(
    (state) => state.listing.sequenceFeatureTypes,
    shallowEqual,
  );

  // will drive the disabled state of each of the blast menu items
  function getBlastEntityMenuItemDisabledTooltip(table?: ITableData) {
    const markerEntity = entityType === ENTITY_TYPE.MARKER;
    if (
      (props.viewType === VIEW.TABLE_VIEW.type && markerEntity) ||
      (props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type && markerEntity)
    ) {
      return `BLAST from marker table is not supported`;
    }

    if (table?.tableQuerySpec?.resultType === ENTITY_TYPE.BED_FEATURE) {
      return `BLAST of ${FEATURE_DISPLAY_NAME[ENTITY_TYPE.BED_FEATURE]} is currently not supported`;
    }

    const variantEntity =
      props.entityType === ENTITY_TYPE.MUTATION ||
      (table && table.tableQuerySpec?.resultType === ENTITY_TYPE.VARIANT) ||
      (table && table.tableQuerySpec?.resultType === ENTITY_TYPE.VCF_VARIANT) ||
      (table && table.tableQuerySpec?.resultType === ENTITY_TYPE.MUTATION);

    if (props.viewType === VIEW.TABLE_VIEW.type && variantEntity) {
      return `BLAST from variant table currently not supported`;
    }
    if (props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type && variantEntity) {
      const variantLength = props.entity?.region
        ? props.entity.region.range.stop - props.entity.region.range.start + 1
        : 0;
      if (variantLength < MINIMUM_BLAST_SEQUENCE_LENGTH) {
        return `This ${props.entityType} is not long enough for BLAST (minimum length: ${MINIMUM_BLAST_SEQUENCE_LENGTH} bp) `;
      }
    }
    if (
      (props.viewType === VIEW.TABLE_VIEW.type || props.viewType === VIEW.GENOME_VIEW.type) &&
      table &&
      table.selectionModel.selectedIds.length === 0
    ) {
      return `At least one row or feature must be selected to perform BLAST.`;
    }
    if (
      props.entityType === ENTITY_TYPE.CONTIG &&
      props.entity &&
      props.entity.length > MAXIMUM_BLAST_SEQUENCE_LENGTH
    ) {
      return `This ${props.entityType} is too long for BLAST (maximum length: ${MAXIMUM_BLAST_SEQUENCE_LENGTH} bp) `;
    }
  }

  function createBlastRegionMenuItem() {
    // applies only to genome View
    if (props.viewType !== VIEW.GENOME_VIEW.type) {
      return null;
    }

    let menuDisabledTooltip;
    const regionLabel = genomeBrowser.selectedRange ? "selected" : "visible";

    if (rangeLength > MAXIMUM_BLAST_SEQUENCE_LENGTH) {
      menuDisabledTooltip = `The ${regionLabel} region is too long for BLAST (maximum: ${MAXIMUM_BLAST_SEQUENCE_LENGTH.toLocaleString()} bp)`;
    } else if (rangeLength < MINIMUM_BLAST_SEQUENCE_LENGTH) {
      menuDisabledTooltip = `The ${regionLabel} region is too short for BLAST (minimum: ${MINIMUM_BLAST_SEQUENCE_LENGTH} bp)`;
    }

    // force to show a tooltip explaining why the button is disabled
    // always wrap the label with a tooltip if it is disabled
    return (
      <Item key={"analyze-button-blast"}>
        <MenuOptionItem
          id={"analyze-button-blast"}
          title={`BLAST ${genomeBrowser.selectedRange ? "Selected" : "Visible"} Region`}
          description={menuDisabledTooltip}
        />
      </Item>
    );
  }

  function createBlastEntityMenuItems() {
    if (props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type) {
      return createBlastMenuItem();
    } else if (props.activeTables) {
      // potentially multiple active tables  (e.g. genome browser case)
      return props.activeTables.map((table, index) => createBlastMenuItem(table, index));
    }
  }

  function getEntityType() {
    // some not officially supported sequence types are not handed in via entityType but have an entity with a type field
    return props.entityType ? props.entityType : props.entity ? props.entity.type : undefined;
  }

  function createBlastMenuItem(table?: ITableData, index?: number) {
    if (
      table &&
      (table.tableQuerySpec?.resultType === ENTITY_TYPE.REGION_FLAG ||
        table.tableQuerySpec?.resultType === ENTITY_TYPE.VCF_VARIANT)
    ) {
      return null;
    }

    const menuDisabledTooltip = getBlastEntityMenuItemDisabledTooltip(table);
    const entityType = getEntityType();
    // some not officially supported sequence types are not handed in via entityType but have an entity with a type field
    const displayName = getSequenceFeatureTypeDisplayName(
      table?.tableQuerySpec as InputTableQuerySpec,
      sequenceFeatureTypes,
    );
    const selectionCount = table?.selectionModel.selectedIds.length;
    const blastMenuItemLabel =
      props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type
        ? "BLAST This " + capitalizeFirstLetterOfAllWords(FEATURE_DISPLAY_NAME[entityType])
        : "BLAST " +
          (selectionCount && selectionCount > 0 ? selectionCount + " " : "") +
          "Selected " +
          capitalizeFirstLetterOfAllWords(pluralizeWord(displayName, selectionCount));

    const data = collectForwardedIds(table as ITableData);

    // force to show a tooltip explaining why the button is disabled
    // always wrap the label with a tooltip if it is disabled
    return (
      <Item key={data.entityType}>
        <MenuOptionItem
          description={menuDisabledTooltip ? menuDisabledTooltip : undefined}
          isDisabled={!!menuDisabledTooltip}
          id={`blast-menu-item-${index}`}
          title={blastMenuItemLabel}
        />
      </Item>
    );
  }

  // drives the disabled state of the Analyze button
  function getAnalyzeButtonDisabledTooltip() {
    const entityType = getEntityType();
    const isTableView = props.viewType === VIEW.TABLE_VIEW.type;
    const isGenomeView = props.viewType === VIEW.GENOME_VIEW.type;

    if (
      isTableView &&
      (entityType === ENTITY_TYPE.VARIANT ||
        entityType === ENTITY_TYPE.MUTATION ||
        entityType === ENTITY_TYPE.PROTEIN_MUTATION ||
        entityType === ENTITY_TYPE.GENE_MUTATION)
    ) {
      return `Single variants with valid sequence properties can be sent to BLAST from their summary page.`;
    }
    if (isTableView && entityType === ENTITY_TYPE.CONTIG) {
      return `Single ${entityType}s with valid sequence properties can be sent to BLAST from their summary page.`;
    }
    if (
      // disabled blast buttons, see ButtonHeaderHoc component for hiding blast buttons in specific contexts
      entityType === ENTITY_TYPE.ORGANISM ||
      entityType === ENTITY_TYPE.SEQUENCE ||
      entityType === ENTITY_TYPE.SAMPLE ||
      entityType === ENTITY_TYPE.BED_FEATURE
    ) {
      return `The current selection cannot be used for BLAST`;
    }
    if (isGenomeView && !props.genomeBrowser.contig) {
      return `Please select a sequence to visualize.`;
    }
  }

  function collectForwardedIds(table: ITableData): { entityType: string; ids?: number[] } {
    if (props.entity) {
      // Send single entity from summary to BLAST
      return { entityType: props.entity.groupType, ids: [props.entity.id] };
    } else {
      return {
        entityType: table.tableQuerySpec?.resultType as string,
        ids: table.selectionModel.selectedIds as number[],
      };
    }
  }

  const entityType = props.entityType;
  const blastEntityMenuDisabledTooltip = props.activeTables?.map(getBlastEntityMenuItemDisabledTooltip).join(""); // No tooltips
  const buttonDisabledTooltip = getAnalyzeButtonDisabledTooltip();
  const tooltip = useMemo(
    () =>
      buttonDisabledTooltip || blastEntityMenuDisabledTooltip || "Perform a BLAST analysis with the current selection",
    [blastEntityMenuDisabledTooltip, buttonDisabledTooltip],
  );

  // flag entities do not show the button at all
  if ((!props.entity && !props.activeTables) || entityType === ENTITY_TYPE.REGION_FLAG) {
    return null;
  }

  const disabledBlastRegion =
    rangeLength > MAXIMUM_BLAST_SEQUENCE_LENGTH || rangeLength < MINIMUM_BLAST_SEQUENCE_LENGTH;

  return (
    <MenuTriggerButton
      icon={<img src={Icons.Action.Blast} width={20} height={20} alt={""} />}
      label={"BLAST"}
      showLabel
      isDisabled={!!(buttonDisabledTooltip || blastEntityMenuDisabledTooltip)}
      key="blast-dialog-popover-btn"
      tooltip={tooltip}
      disabledKeys={disabledBlastRegion ? ["analyze-button-blast"] : undefined}
      onAction={(key) => {
        switch (key) {
          case "analyze-button-blast":
            void dispatch(
              forwardContigRangeToBlast(
                props.genomeBrowser.contig,
                props.genomeBrowser.selectedRange || props.genomeBrowser.range,
              ),
            );
            break;
          default:
            // eslint-disable-next-line no-case-declarations
            const data = props.activeTables?.map(collectForwardedIds).filter((t) => t.entityType === key)?.[0];

            // If no data, this is a summary page
            dispatch(
              data
                ? forwardFeaturesToBlast(data.entityType, data.ids || [])
                : forwardFeaturesToBlast(props.entity.groupType, [props.entity.id]),
            );
            break;
        }
      }}
    >
      <>
        {createBlastRegionMenuItem()}
        {createBlastEntityMenuItems()}
      </>
    </MenuTriggerButton>
  );
}
